import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Export, ShareNetwork, Lock } from '@phosphor-icons/react';
import { playingHandicap, effectiveHolePars, effectiveStrokeIndexes, shotsOnHole, computeRoundLeaderboardNSP, isRoundFrozen, revealHoleForRound, startHoleForGroup, holeInPlayingRange, groupIndexForPlayer, stablefordPoints, COUNTBACK_SLICES, playerHoleSum, compareCountback, nspCountbackFromNets, compareCountbackNSP, formatHandicapIndex, formatPlayingHandicap, getEffectivePH, getAllowedPH, displayWHS, s36Handicap, effectivePHForBasis } from '../golf';
import { effectiveCourseForPlayer, effectiveStrokeIndexesForPlayer, effectiveHoleParsForPlayer } from '../lib/effectiveCourse';
import { roundFormat, formatValueLabel } from '../lib/formatHelpers';
import { getTripAccess } from '../lib/tripAccess';
import { toPng } from 'html-to-image';

function formatDateRange(start, end) {
  const fmt = (s) => { if (!s) return null; const d = new Date(s + 'T12:00:00'); return isNaN(d) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); };
  const a = fmt(start), b = fmt(end);
  if (!a && !b) return '';
  if (!a) return b;
  if (!b || a === b) return a;
  // Same year: "30 Oct to 1 Nov 2026"
  const da = new Date(start + 'T12:00:00'), db = new Date(end + 'T12:00:00');
  if (da.getFullYear() === db.getFullYear()) {
    const s = da.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `${s} to ${b}`;
  }
  return `${a} to ${b}`;
}

function scoreColorClass(strokes, par) {
  if (!strokes || strokes <= 0) return 'score-unscored';
  const diff = strokes - par;
  if (diff <= -2) return 'score-eagle';
  if (diff === -1) return 'score-birdie';
  if (diff === 0) return 'score-par';
  if (diff === 1) return 'score-bogey';
  if (diff === 2) return 'score-double-bogey';
  return 'score-triple-plus';
}

export default function ScorecardView() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const cacheKey = `foretour:trip:${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [round, setRound] = useState(() => trip?.rounds?.find(r => r.id === roundId) || null);
  const [loading, setLoading] = useState(!trip || !round);
  const [exporting, setExporting] = useState(false);
  const [displayMode, setDisplayMode] = useState('strokes');
  const [filterMode, setFilterMode] = useState('all');
  const [filterGroupIdx, setFilterGroupIdx] = useState(null);
  const [filterPlayerId, setFilterPlayerId] = useState(null);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      setTrip(res.data);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(res.data)); } catch {}
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setLoading(false); }
  }, [code, roundId, cacheKey]);
  useEffect(() => { load(); }, [load]);

  const players = trip?.players || [];
  const holePars = round ? effectiveHolePars(round) : Array(18).fill(4);
  const strokeIndexes = round ? effectiveStrokeIndexes(round) : Array.from({length:18}, (_,i) => i+1);
  const outPar = holePars.slice(0, 9).reduce((s, v) => s + v, 0);
  const inPar = holePars.slice(9).reduce((s, v) => s + v, 0);
  const isFrozen = round ? isRoundFrozen(round) : false;
  const freezeAt = round ? revealHoleForRound(round) : 18;
  const isStableford = round ? roundFormat(round) !== 'nsp' : false;

  // ── Freeze visibility: server-side null scrub handles data confidentiality.
  // Frontend shows every player; lock icons come from frozenHoles set. ──
  const access = getTripAccess(code);
  const isOrganiser = access?.role === 'organiser' || !!access?.legacy;
  const showFreezeHidden = false;
  const freezeGroupPids = null;

  const handleExport = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      // Temporarily expand card to full table width for uncropped capture
      const card = cardRef.current;
      const tableWrap = card.querySelector('[data-scroll-wrap]');
      card.style.minWidth = '860px';
      card.style.maxWidth = 'none';
      if (tableWrap) tableWrap.style.overflow = 'visible';
      const dataUrl = await toPng(card, {
        backgroundColor: '#FFFFFF',
        pixelRatio: 2,
        cacheBust: true,
        style: { padding: '16px' },
      });
      // Restore mobile constraints
      card.style.minWidth = '';
      card.style.maxWidth = '';
      if (tableWrap) tableWrap.style.overflow = '';
      // Try Web Share API first
      if (navigator.share && navigator.canShare) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], `${trip?.name}-${round?.label}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title: `${trip?.name} — ${round?.label}`, files: [file] });
            setExporting(false);
            return;
          }
        } catch {}
      }
      // Fallback: download
      const link = document.createElement('a');
      link.download = `${trip?.name || 'scorecard'}-${round?.label || 'round'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('Export failed', e); }
    finally { setExporting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="scorecard-skeleton">
      <div className="h-48 bg-white animate-pulse border-b border-[#E1E6E4]" />
      <div className="px-4 pt-4 max-w-lg mx-auto space-y-3">
        {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}
      </div>
    </div>
  );
  if (!round) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  // Detect multi-tee from existing round data
  const playerTees = round.player_tees || {};
  const uniqueTees = new Set(Object.values(playerTees));
  const hasMultipleTees = uniqueTees.size > 1;

  // Build group list from existing round data
  const groups = (round.teams || []).map((pids, i) => ({
    idx: i, label: `Group ${i + 1}`, pids,
  }));

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="scorecard-screen">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">Scorecard</h1><p className="text-xs text-[#B8C7C0]">{round.label} · {round.course?.name}</p></div>
        <Button size="sm" onClick={handleExport} disabled={exporting} className="bg-[#1B4332] text-white h-8 rounded-lg gap-1" data-testid="export-btn">
          <ShareNetwork className="w-4 h-4" weight="bold" /> {exporting ? '...' : 'Share'}
        </Button>
      </div>

      {/* Strokes / Points toggle — Stableford only */}
      {isStableford && (
        <div className="flex justify-center px-4 pt-3 pb-1" data-testid="scorecard-display-toggle">
          <div className="inline-flex rounded-lg bg-[#E1E6E4] p-0.5">
            <button onClick={() => setDisplayMode('strokes')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${displayMode === 'strokes' ? 'bg-white text-[#1D242B] shadow-sm' : 'text-[#455561]'}`}
              data-testid="toggle-strokes">Strokes</button>
            <button onClick={() => setDisplayMode('points')}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${displayMode === 'points' ? 'bg-white text-[#1D242B] shadow-sm' : 'text-[#455561]'}`}
              data-testid="toggle-points">Stableford</button>
          </div>
        </div>
      )}

      <div className="px-2 py-4">
        <div ref={cardRef} className="max-w-full mx-auto bg-white rounded-xl border border-[#E1E6E4] overflow-hidden">
          {/* Header with branding */}
          <div className="bg-[#1B4332] text-white px-4 py-3 relative">
            <h2 className="text-sm font-bold leading-snug" style={{overflowWrap: 'break-word'}}>{trip?.name}</h2>
            {(trip?.start_date || trip?.end_date) && (
              <p className="text-[10px] text-[#B8C7C0] mt-0.5">{formatDateRange(trip.start_date, trip.end_date)}</p>
            )}
            <p className="text-xs text-white/80 mt-1">{round.label} · {round.course?.name}</p>
            <p className="text-[10px] text-[#B8C7C0] mt-0.5">
              <span className="whitespace-nowrap">Par&nbsp;{round.course?.par}</span>
              {!hasMultipleTees && round.course?.tee_name && <> · {round.course.tee_name}</>}
              {!hasMultipleTees && round.course?.course_rating && <> · <span className="whitespace-nowrap">CR {round.course.course_rating}</span></>}
              {!hasMultipleTees && round.course?.slope_rating && <> · <span className="whitespace-nowrap">Slope {round.course.slope_rating}</span></>}
            </p>
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#C9A24A] text-[#1B4332]">
              {roundFormat(round) === 'nsp' ? 'Net Stroke Play' : 'Stableford'}
            </span>
            {roundFormat(round) === 'nsp' && round.handicap_basis === 'system36' && (
              <span className="inline-block ml-1.5 mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F2FD] text-[#1565C0]" data-testid="s36-badge">System 36</span>
            )}
            {roundFormat(round) === 'nsp' && round.handicap_basis === 'lower_of' && (
              <span className="inline-block ml-1.5 mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E3F2FD] text-[#1565C0]" data-testid="lower-of-badge">Lower Of</span>
            )}
            {round?.handicap_allowance_pct != null && round.handicap_allowance_pct < 100 && round.handicap_basis !== 'system36' && (
              <span className="inline-block ml-1.5 mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#FFF3E0] text-[#E59000]">
                Allowance {round.handicap_allowance_pct}%
              </span>
            )}
            <p className="text-[10px] font-bold text-[#C9A24A] absolute bottom-3 right-4">Fore Tour</p>
          </div>

          {/* Freeze-restricted: hide scores for non-organiser without own-group claim */}
          {showFreezeHidden ? (
            <div className="px-4 py-12 text-center" data-testid="scorecard-frozen-hidden">
              <Lock className="w-8 h-8 text-[#B8C7C0] mx-auto mb-3" weight="bold" />
              <p className="text-[#455561] text-sm">Scores are hidden until the organiser reveals the results.</p>
            </div>
          ) : (
          <>
          {/* Filter control — hidden when freeze restricts to own group */}
          {!freezeGroupPids && (
          <div className="px-2 py-1.5 flex items-center gap-1.5 flex-wrap bg-[#F4F6F5] border-b border-[#E1E6E4]" data-testid="scorecard-filter">
            {['all', 'group', 'player'].map(m => (
              <button key={m} onClick={() => { setFilterMode(m); if (m === 'all') { setFilterGroupIdx(null); setFilterPlayerId(null); } }}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${filterMode === m ? 'bg-[#1B4332] text-white' : 'bg-white text-[#455561] border border-[#E1E6E4]'}`}
                data-testid={`filter-${m}`}>
                {m === 'all' ? 'All players' : m === 'group' ? 'Group' : 'Player'}
              </button>
            ))}
            {filterMode === 'group' && groups.length > 0 && (
              <select value={filterGroupIdx ?? ''} onChange={e => setFilterGroupIdx(e.target.value === '' ? null : Number(e.target.value))}
                className="px-2 py-1 rounded-md text-[11px] border border-[#E1E6E4] bg-white text-[#1D242B] min-w-0"
                data-testid="filter-group-select">
                <option value="">Choose group</option>
                {groups.map(g => <option key={g.idx} value={g.idx}>{g.label} ({g.pids.length})</option>)}
              </select>
            )}
            {filterMode === 'player' && (
              <select value={filterPlayerId ?? ''} onChange={e => setFilterPlayerId(e.target.value || null)}
                className="px-2 py-1 rounded-md text-[11px] border border-[#E1E6E4] bg-white text-[#1D242B] min-w-0"
                data-testid="filter-player-select">
                <option value="">Choose player</option>
                {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
          </div>
          )}

          {/* Scorecard table(s) — split by tee when players are on different tees */}
          <div className="p-2 overflow-x-auto" data-scroll-wrap>
            {(() => {
              // ── Sort + countback (global, across all tees) ──
              let sortedPlayers = players;
              const playerStats = {};
              const isNSPFormat = round ? roundFormat(round) === 'nsp' : false;
              const roundFrozen = round ? isRoundFrozen(round) : false;

              if (round && (isStableford || isNSPFormat)) {
                const frozenUnrevealed = roundFrozen;
                if (!frozenUnrevealed) {
                for (const p of players) {
                  if (isStableford) {
                    const scores = round.hole_scores?.[p.id] || Array(18).fill(-1);
                    const pNr = round.hole_nr?.[p.id] || [];
                    const total = scores.reduce((s, v, i) => s + (pNr.includes(i) ? 0 : Math.max(0, v)), 0);
                    const cbTuple = COUNTBACK_SLICES.map(slice => playerHoleSum(round, p.id, slice));
                    playerStats[p.id] = { total, cbTuple };
                  } else {
                    const strokes = round.hole_strokes?.[p.id] || [];
                    const course = effectiveCourseForPlayer(round, p.id);
                    const enteredPH = getAllowedPH(p, course, round);
                    const si = effectiveStrokeIndexesForPlayer(round, p.id);
                    const pars = effectiveHolePars(round);
                    const basis = round.handicap_basis;
                    const isS36B = basis === 'system36' || basis === 'lower_of';
                    const s36H = isS36B ? s36Handicap(strokes, pars, round.s36_max_handicap ?? null) : null;
                    const { ph: effPH } = effectivePHForBasis(basis, enteredPH, s36H);
                    const ph = effPH ?? 0;
                    const pending = isS36B && effPH == null;
                    let net = 0; let thru = 0;
                    const holeNets = [];
                    for (let h = 0; h < 18; h++) {
                      const s = strokes[h];
                      if (s != null && s > 0) { net += s - shotsOnHole(ph, si[h]); holeNets.push(s - shotsOnHole(ph, si[h])); thru++; }
                      else { holeNets.push(0); }
                    }
                    const cbTuple = nspCountbackFromNets(holeNets);
                    playerStats[p.id] = { total: net, cbTuple, thru, s36_pending: pending };
                  }
                }
                sortedPlayers = [...players].sort((a, b) => {
                  const aStats = playerStats[a.id]; const bStats = playerStats[b.id];
                  if (!aStats || !bStats) return 0;
                  if (isStableford) {
                    const diff = bStats.total - aStats.total;
                    if (diff !== 0) return diff;
                    return compareCountback(aStats.cbTuple, bStats.cbTuple);
                  } else {
                    if (!aStats.thru && bStats.thru) return 1;
                    if (aStats.thru && !bStats.thru) return -1;
                    if (aStats.total !== bStats.total) return aStats.total - bStats.total;
                    return compareCountbackNSP(aStats.cbTuple, bStats.cbTuple);
                  }
                });
                } // end !frozenUnrevealed score sort

                // When frozen + unrevealed: sort by group ascending, within-group order
                if (frozenUnrevealed) {
                  const teams = round.teams || [];
                  const playerGroupMap = new Map();
                  teams.forEach((pids, gIdx) => (pids || []).forEach((pid, posInGroup) => playerGroupMap.set(pid, { gIdx, posInGroup })));
                  sortedPlayers = [...players].sort((a, b) => {
                    const ga = playerGroupMap.get(a.id);
                    const gb = playerGroupMap.get(b.id);
                    if (!ga && !gb) return 0;
                    if (!ga) return 1;
                    if (!gb) return -1;
                    if (ga.gIdx !== gb.gIdx) return ga.gIdx - gb.gIdx;
                    return ga.posInGroup - gb.posInGroup;
                  });
                }
              }

              // ── Apply display filter (display-only, does not change rankings/CB) ──
              let filteredPlayers = sortedPlayers;
              if (freezeGroupPids) {
                // Freeze restriction: show only own group
                filteredPlayers = sortedPlayers.filter(p => freezeGroupPids.has(p.id));
              } else if (filterMode === 'group' && filterGroupIdx !== null) {
                const groupPids = new Set(groups[filterGroupIdx]?.pids || []);
                filteredPlayers = sortedPlayers.filter(p => groupPids.has(p.id));
              } else if (filterMode === 'player' && filterPlayerId) {
                filteredPlayers = sortedPlayers.filter(p => p.id === filterPlayerId);
              }

              // ── CB highlight (global) ──
              const cbHighlight = {};
              const CB_LABELS = ['Back 9', 'Back 6', 'Back 3', 'H18', 'H17'];
              if ((isStableford || isNSPFormat) && !roundFrozen) {
                let i = 0;
                while (i < sortedPlayers.length) {
                  const total = playerStats[sortedPlayers[i].id]?.total ?? -Infinity;
                  let j = i + 1;
                  while (j < sortedPlayers.length && playerStats[sortedPlayers[j].id]?.total === total) j++;
                  if (j - i >= 2) {
                    const group = sortedPlayers.slice(i, j);
                    let deepestSliceIdx = -1;
                    for (let a = 0; a < group.length; a++) {
                      for (let b = a + 1; b < group.length; b++) {
                        const cbA = playerStats[group[a].id].cbTuple;
                        const cbB = playerStats[group[b].id].cbTuple;
                        for (let s = 0; s < cbA.length; s++) {
                          if (cbA[s] !== cbB[s]) { deepestSliceIdx = Math.max(deepestSliceIdx, s); break; }
                        }
                      }
                    }
                    if (deepestSliceIdx >= 0) {
                      const holes = new Set(COUNTBACK_SLICES[deepestSliceIdx]);
                      const label = CB_LABELS[deepestSliceIdx] || 'CB';
                      for (const p of group) cbHighlight[p.id] = { holes, label };
                    }
                  }
                  i = j;
                }
              }

              // ── Group players by effective tee name ──
              const teeGroups = new Map();
              for (const p of sortedPlayers) {
                const teeName = round.player_tees?.[p.id] ?? round.course?.tee_name ?? null;
                const key = teeName || '__default__';
                if (!teeGroups.has(key)) teeGroups.set(key, []);
                teeGroups.get(key).push(p);
              }
              const isSingleTee = teeGroups.size <= 1;

              // ── Render a single scorecard table for a group of players ──
              const renderTable = (tablePlayers, teeLabel, teeCR, teeSlope, teePars, teeSI, tableIdx) => {
                const teeOutPar = teePars.slice(0, 9).reduce((s, v) => s + v, 0);
                const teeInPar = teePars.slice(9).reduce((s, v) => s + v, 0);
                return (
                  <table key={tableIdx} className="w-full text-xs border-collapse" data-testid={`scorecard-table${isSingleTee ? '' : `-${tableIdx}`}`}>
                    <thead>
                      <tr className="bg-[#1B4332] text-white">
                        <th className="px-2 py-2 text-left font-semibold w-28 sticky left-0 bg-[#1B4332] z-10">
                          {isSingleTee ? 'Player' : (
                            <div>
                              <div className="font-bold">{teeLabel}</div>
                              {(teeCR || teeSlope) && (
                                <div className="font-normal text-[9px] text-white/70 mt-0.5">
                                  {teeCR && <span className="whitespace-nowrap">CR {teeCR}</span>}
                                  {teeCR && teeSlope && ' · '}
                                  {teeSlope && <span className="whitespace-nowrap">Slope {teeSlope}</span>}
                                </div>
                              )}
                            </div>
                          )}
                        </th>
                        {Array.from({length:9}, (_,i) => <th key={i} className="px-1 py-2 text-center font-semibold w-8">{i+1}</th>)}
                        <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">OUT</th>
                        {Array.from({length:9}, (_,i) => <th key={i+9} className="px-1 py-2 text-center font-semibold w-8">{i+10}</th>)}
                        <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">IN</th>
                        <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">{isFrozen ? `Thru ${freezeAt}` : 'Total'}</th>
                        <th className="px-2 py-2 text-center font-bold bg-[#1B4332] w-10">{isStableford && displayMode === 'strokes' ? 'Net' : formatValueLabel(roundFormat(round))}</th>
                      </tr>
                      <tr className="bg-[#F4F6F5]">
                        <td className="px-2 py-1.5 font-semibold text-[#455561] sticky left-0 bg-[#F4F6F5] z-10">Par</td>
                        {teePars.slice(0,9).map((p,i) => <td key={i} className="text-center text-[#455561] font-semibold">{p}</td>)}
                        <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{teeOutPar}</td>
                        {teePars.slice(9).map((p,i) => <td key={i+9} className="text-center text-[#455561] font-semibold">{p}</td>)}
                        <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{teeInPar}</td>
                        <td className="text-center font-bold text-[#455561] bg-[#E9EDEB]">{teeOutPar+teeInPar}</td>
                        <td className="bg-[#E9EDEB]"></td>
                      </tr>
                      <tr className="bg-[#F4F6F5]">
                        <td className="px-2 py-1 text-[10px] text-[#B8C7C0] sticky left-0 bg-[#F4F6F5] z-10">SI</td>
                        {teeSI.slice(0,9).map((s,i) => <td key={i} className="text-center text-[10px] text-[#B8C7C0]">{s}</td>)}
                        <td className="bg-[#E9EDEB]"></td>
                        {teeSI.slice(9).map((s,i) => <td key={i+9} className="text-center text-[10px] text-[#B8C7C0]">{s}</td>)}
                        <td className="bg-[#E9EDEB]"></td><td className="bg-[#E9EDEB]"></td><td className="bg-[#E9EDEB]"></td>
                      </tr>
                    </thead>
                    <tbody>
                      {tablePlayers.map(player => {
                        const strokes = round.hole_strokes?.[player.id] || Array(18).fill(0);
                        const scores = round.hole_scores?.[player.id] || Array(18).fill(-1);
                        const playerCourse = effectiveCourseForPlayer(round, player.id);
                        const enteredPH = getAllowedPH(player, playerCourse, round);
                        const teeName = round.player_tees?.[player.id] ?? round.course?.tee_name ?? null;
                        const isNSP = roundFormat(round) === 'nsp';
                        const playerSI = effectiveStrokeIndexesForPlayer(round, player.id);
                        // S36 basis-aware PH
                        const scBasis = round.handicap_basis;
                        const scIsS36 = scBasis === 'system36' || scBasis === 'lower_of';
                        const scPars = effectiveHolePars(round);
                        const scS36H = scIsS36 ? s36Handicap(strokes, scPars, round.s36_max_handicap ?? null) : null;
                        const { ph: scEffPH, source: scSource, s36_handicap: scS36Hcap } = effectivePHForBasis(scBasis, enteredPH, scS36H);
                        const ph = scEffPH ?? 0;
                        const scPending = scIsS36 && scEffPH == null;
                        const isNR = (round.nr_players || []).includes(player.id);
                        const frozen = isRoundFrozen(round);
                        const freezeHole = revealHoleForRound(round);
                        const gIdx = groupIndexForPlayer(round, player.id);
                        const throughPos = frozen ? freezeHole : 18;
                        let frozenHoles = new Set();
                        if (frozen) {
                          for (let h = 0; h < 18; h++) {
                            if (!holeInPlayingRange(round, gIdx, h, throughPos)) frozenHoles.add(h);
                          }
                        }
                        const playerHoleNr = round.hole_nr?.[player.id] || [];
                        // Net Double Bogey gross for an NR hole: par + 2 + strokes received
                        const ndbGross = (h) => teePars[h] + 2 + shotsOnHole(ph, playerSI[h]);
                        const holePoints = isStableford ? strokes.map((s, i) =>
                          frozenHoles.has(i) || playerHoleNr.includes(i) ? null : stablefordPoints(s > 0 ? s : null, teePars[i], ph, playerSI[i])
                        ) : null;
                        const outStrokes = strokes.slice(0,9).reduce((s,v,i) => {
                          if (frozenHoles.has(i)) return s;
                          if (playerHoleNr.includes(i)) return s + ndbGross(i);
                          return s + v;
                        }, 0);
                        const inStrokes = strokes.slice(9).reduce((s,v,i) => {
                          if (frozenHoles.has(i+9)) return s;
                          if (playerHoleNr.includes(i+9)) return s + ndbGross(i+9);
                          return s + v;
                        }, 0);
                        const outPoints = holePoints ? holePoints.slice(0,9).reduce((s,v) => s + (v ?? 0), 0) : 0;
                        const inPoints = holePoints ? holePoints.slice(9).reduce((s,v) => s + (v ?? 0), 0) : 0;
                        let finalCol;
                        if (isNSP) {
                          let net = 0;
                          for (let h = 0; h < 18; h++) {
                            if (frozenHoles.has(h)) continue;
                            if (strokes[h] > 0) net += strokes[h] - shotsOnHole(ph, playerSI[h]);
                          }
                          finalCol = net;
                        } else {
                          finalCol = scores.reduce((s,v,i) => s + (frozenHoles.has(i) || playerHoleNr.includes(i) ? 0 : Math.max(0, v)), 0);
                        }
                        const cbData = cbHighlight[player.id] || null;
                        const cbHoles = cbData?.holes || null;
                        const renderCell = (s, holeIdx) => {
                          if (frozenHoles.has(holeIdx)) {
                            return (
                              <td key={holeIdx} className="text-center py-1.5">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-xs text-[#B8C7C0] bg-[#F4F6F5]" title="Hidden (frozen)">
                                  &#x1F512;
                                </span>
                              </td>
                            );
                          }
                          if (playerHoleNr.includes(holeIdx)) {
                            const showNdb = round.revealed && isStableford && displayMode === 'strokes';
                            const ndb = ndbGross(holeIdx);
                            return (
                              <td key={holeIdx} className="text-center py-1.5">
                                {showNdb ? (
                                  <span className="inline-flex flex-col items-center justify-center w-7 rounded-md" title={`NR · NDB ${ndb}`}>
                                    <span className="text-[9px] font-bold text-[#E59000]">{ndb}</span>
                                    <span className="text-[6px] font-bold text-[#E59000] leading-none">NR</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[9px] font-bold text-[#E59000] bg-[#FFF3E0]" title="No Return">NR</span>
                                )}
                              </td>
                            );
                          }
                          const showPoints = isStableford && displayMode === 'points';
                          const pts = holePoints ? holePoints[holeIdx] : null;
                          const val = showPoints ? pts : s;
                          const empty = showPoints ? (pts == null) : (s <= 0);
                          const cbTint = cbHoles && cbHoles.has(holeIdx) ? 'bg-amber-50' : '';
                          return (
                            <td key={holeIdx} className={`text-center py-1.5 ${cbTint}`}>
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold ${scoreColorClass(s, teePars[holeIdx])}`}>
                                {empty ? '-' : val}
                              </span>
                            </td>
                          );
                        };
                        return (
                          <tr key={player.id} className="border-t border-[#E1E6E4]" data-testid={`sc-row-${player.id}`}>
                            <td className="px-2 py-2 sticky left-0 bg-white z-10 min-w-[110px] max-w-[140px]">
                              <div className="font-semibold text-[#1D242B] whitespace-normal">{player.name}</div>
                              {scIsS36 && isNSP ? (
                                scPending ? (
                                  <div className="text-[10px] text-[#8A9BA3] font-semibold">S36 – · Gross {strokes.reduce((a,v) => a + (v > 0 ? v : 0), 0) || '–'}</div>
                                ) : scBasis === 'lower_of' ? (
                                  <div className="text-[10px] text-[#1B4332] font-semibold">
                                    <span className={scSource === 'entered_at_allowance' ? 'font-black' : ''}>PH {formatPlayingHandicap(enteredPH)}</span>
                                    {' · '}
                                    <span className={scSource === 's36' ? 'font-black' : ''}>S36 {formatPlayingHandicap(scS36Hcap)}</span>
                                  </div>
                                ) : (
                                  <div className="text-[10px] text-[#1B4332] font-semibold">S36 {formatPlayingHandicap(scS36Hcap)}</div>
                                )
                              ) : (
                                <div className="text-[10px] text-[#1B4332] font-semibold">PH {formatPlayingHandicap(ph)} · WHS {displayWHS(player)}</div>
                              )}
                              {teeName && isSingleTee && <div className="text-[9px] text-[#455561] truncate">{teeName}</div>}
                              {trip?.divisions_enabled && (() => { const div = (trip?.divisions || []).find(d => d.id === player.division_id); return div ? <div className="text-[9px] text-[#8A9BA3] font-semibold">{div.name}</div> : null; })()}
                              {cbData && <div className="text-[9px] font-bold text-amber-600 mt-0.5">CB · {cbData.label}</div>}
                            </td>
                            {strokes.slice(0,9).map((s,i) => renderCell(s, i))}
                            <td className="text-center font-bold bg-[#F4F6F5] py-1">
                              {isStableford && displayMode === 'points'
                                ? <span className="text-[#1B4332]">{outPoints}</span>
                                : <span className="text-[#1D242B]">{outStrokes || '-'}</span>}
                            </td>
                            {strokes.slice(9).map((s,i) => renderCell(s, i+9))}
                            <td className="text-center font-bold bg-[#F4F6F5] py-1">
                              {isStableford && displayMode === 'points'
                                ? <span className="text-[#1B4332]">{inPoints}</span>
                                : <span className="text-[#1D242B]">{inStrokes || '-'}</span>}
                            </td>
                            <td className="text-center font-bold bg-[#F4F6F5] py-1">
                              {isStableford && displayMode === 'points'
                                ? <span className="text-[#1B4332]">{outPoints + inPoints}</span>
                                : <span className="text-[#1D242B]">{(outStrokes+inStrokes) || '-'}</span>}
                            </td>
                            <td className="text-center font-bold text-[#1B4332] bg-[#E8E5DB]">{isNR ? 'NR' : (scPending && isNSP) ? '–' : isStableford && displayMode === 'strokes' ? ((outStrokes + inStrokes) - ph) || '-' : finalCol}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              };

              // ── Render: single table or split by tee ──
              const filterSet = freezeGroupPids
                ? freezeGroupPids
                : filterMode === 'group' && filterGroupIdx !== null
                  ? new Set(groups[filterGroupIdx]?.pids || [])
                  : filterMode === 'player' && filterPlayerId
                    ? new Set([filterPlayerId])
                    : null;

              if (isSingleTee) {
                return renderTable(filteredPlayers, 'Player', null, null, holePars, strokeIndexes, 0);
              }
              let tableIdx = 0;
              return Array.from(teeGroups.entries()).map(([teeKey, teePlayers]) => {
                const displayPlayers = filterSet ? teePlayers.filter(p => filterSet.has(p.id)) : teePlayers;
                if (displayPlayers.length === 0) return null;
                const refPlayer = teePlayers[0];
                const teeHP = effectiveHoleParsForPlayer(round, refPlayer.id);
                const teeSI = effectiveStrokeIndexesForPlayer(round, refPlayer.id);
                const teeObj = round.course?.tees?.find(t => t.name === (teeKey === '__default__' ? round.course?.tee_name : teeKey));
                const label = teeKey === '__default__' ? (round.course?.tee_name || 'Default') : teeKey;
                const cr = teeObj?.course_rating || null;
                const slope = teeObj?.slope_rating || null;
                return renderTable(displayPlayers, label, cr, slope, teeHP, teeSI, tableIdx++);
              });
            })()}
          </div>
          </>
          )}

          {/* Branding footer */}
          <div className="bg-[#F4F6F5] text-center px-4 py-2 border-t border-[#E1E6E4]">
            <p className="text-[10px] text-[#455561] font-semibold">Fore Tour™ — Golf Scoring & Management · foretour.co.uk</p>
          </div>
        </div>
      </div>
    </div>
  );
}
